import { Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, memo, useMemo } from "react";
import { Control, useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { CollapsibleCard } from "@/components/elements/CollapsibleCard";
import { EMPTY_STR } from "@/constants/string";
import { SegmentedControl } from "@/modules/irohana-components/elements/SegmentedControl";
import { Table } from "@/modules/irohana-components/elements/Table";
import { useTable } from "@/modules/irohana-components/hooks/useTable";
import { text } from "@/theme/colors";
import {
  ADD_ROW_LABEL,
  PRICE_DIMENSION_LABEL,
  PRODUCT_FORM_TABLE_MAX_HEIGHT,
} from "@/features/organization/product/constants/productEnums";
import { TProductForm } from "@/api/services/main/organization/product/dtos/product.dto";
import { emptyTypePriceForm } from "@/features/organization/product/utils/productForm";
import { parseUnitPrice } from "@/features/organization/product/utils/productUtils";
import {
  TProductTypePriceRow,
  useProductTypePriceSection,
} from "@/features/organization/product/hooks/useProductTypePriceSection";
import { useProductMasters } from "@/features/organization/product/hooks/useProductMasters";

const HELPER_TEXT = "国籍、分野、ティアに各単価を設定できます。未設定の項目は基本単価となります。";

type TProductTypePriceSectionProps = { isTierDiscountTarget: boolean };

/** 範囲人数 (readonly) — inclusive headcount of the ティア range. TODO(figma 309-82521): confirm formula. */
const rangeCount = (lower: string | null, upper: string | null): number | null => {
  const low = parseUnitPrice(lower);
  const high = parseUnitPrice(upper);
  if (low == null || high == null || high < low) return null;
  return high - low + 1;
};

const RangeCountCell: FC<{ control: Control<TProductForm>; index: number }> = ({ control, index }) => {
  const lower = useWatch({ control, name: `typePrices.${index}.lowerLimit` });
  const upper = useWatch({ control, name: `typePrices.${index}.upperLimit` });
  const count = rangeCount(lower, upper);
  return (
    <Typography variant="body14" color={text.primary} textAlign="right">
      {count == null ? EMPTY_STR.TEXT : `${count}名`}
    </Typography>
  );
};

/** 種別単価設定 — SegmentedControl + editable price table (登録・編集). */
export const ProductTypePriceSection: FC<TProductTypePriceSectionProps> = memo(({ isTierDiscountTarget }) => {
  const { control } = useFormContext<TProductForm>();
  const { nationalityOptions, industryOptions } = useProductMasters();
  const productName = useWatch({ control, name: "name" }) ?? "";
  const watchedTypePrices = useWatch({ control, name: "typePrices" }) ?? [];
  const { fields, append, prepend, remove } = useFieldArray({ control, name: "typePrices" });

  const typePriceRows = useMemo<TProductTypePriceRow[]>(
    () => fields.map((field, index) => ({ ...field, _idx: index })),
    [fields],
  );

  const rangeCountCell: MRT_ColumnDef<TProductTypePriceRow>["Cell"] = ({ row }) => (
    <RangeCountCell control={control} index={row.original._idx} />
  );

  const {
    availableDimensions,
    selectedDimension,
    setSelectedDimension,
    dimensionRows,
    columns,
  } = useProductTypePriceSection({
    control,
    productName,
    isTierDiscountTarget,
    fields: typePriceRows,
    watchedTypePrices,
    prepend,
    remove,
    rangeCountCell,
    nationalityOptions,
    industryOptions,
  });

  const { table } = useTable({ rows: dimensionRows, columns, getRowId: (row) => row.id, pinningColumns: ["action"] });
  const enhancedTable = useMemo(() => ({ ...table, addNewRow: () => append(emptyTypePriceForm(selectedDimension)) }), [table, append, selectedDimension]);
  return (
    <CollapsibleCard
      title="種別単価設定"
      description={HELPER_TEXT}
      showBorder={false}
      headerAction={
        <SegmentedControl
          value={selectedDimension}
          onChange={setSelectedDimension}
          options={availableDimensions.map((dimension) => ({ value: dimension, label: PRICE_DIMENSION_LABEL[dimension] }))}
        />
      }
    >
      <Table
        table={enhancedTable}
        addBtnText={ADD_ROW_LABEL}
        maxHeight={PRODUCT_FORM_TABLE_MAX_HEIGHT}
      />
    </CollapsibleCard>
  );
});

ProductTypePriceSection.displayName = "ProductTypePriceSection";
